/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback } from 'react';
// eslint-disable-next-line jira/restricted/@jira+issue__context-service
import { useIssueKey, useAnalyticsSource } from '@jira/issue__context-service/src/useIssueKey.tsx';
import { useRightSidebarController } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createStore, createHook, createContainer } from '@atlassian/react-sweet-state';
import { EMBEDDED_CONFLUENCE_SIDEPANEL_CONTAINER_ID } from '../../constants.tsx';
import { actions, initialState } from './actions.tsx';
import {
	DisplayTypes,
	type OpenSidePanelCoreParams,
	type EmbeddedConfluenceState,
	type UseEmbeddedConfluenceSidePanelType,
} from './types.tsx';
import { getDisplayInformation } from './utils.tsx';

export type Actions = typeof actions;

/**
 * Container for {@link useEmbeddedConfluenceSidePanel}. Any component that uses this hook
 * must be wrapped in this container.
 */
const Container = createContainer();

const DefaultStore = createStore<EmbeddedConfluenceState, Actions>({
	actions,
	containedBy: Container,
	initialState,
	name: EMBEDDED_CONFLUENCE_SIDEPANEL_CONTAINER_ID,
});

export const EmbeddedConfluenceSidepanelContainer = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	const issueKey = useIssueKey();

	/*
	In order for each usage of the store to manage the same state irregardless of
	their location in the component tree, we need to wrap components in a container
	with the same scope value. This is why we use the issueKey in the scope value.
	see https://atlassian.github.io/react-sweet-state/#/advanced/container?id=scoped-data-sharing
	for more information about scoped data sharing.

	*/
	return <Container scope={`embedded-confluence-sidepanel-${issueKey}`}>{children}</Container>;
};

/**
 * Scopes the side-panel store by the `issueKey` passed as a prop, so the panel
 * can be mounted **outside** the issue/work-item provider tree (e.g. the shared
 * layout right-sidebar) without reading `useIssueKey()` / IssueContextContainer.
 * Selected behind `remove-issue-context-mirror-on-wiv` via `componentWithFG`
 * in SafeEmbeddedConfluencePanel.
 */
export const EmbeddedConfluenceSidepanelContainerWithIssueKey = ({
	children,
	issueKey,
}: {
	children: React.ReactNode;
	issueKey?: string;
}): React.JSX.Element => (
	<Container scope={`embedded-confluence-sidepanel-${issueKey}`}>{children}</Container>
);

const useDefaultStoreHook = createHook(DefaultStore);

export const useEmbeddedConfluenceSidePanel: UseEmbeddedConfluenceSidePanelType = (
	embeddedConfluenceSource?: string,
) => {
	const [
		state,
		{
			closeSidePanel: closeSidePanelAction,
			openSidePanel: openSidePanelAction,
			setIsLoadingForDeferSpaceSelection,
			setDeferredSpaceData,
			toggleExpandedState,
			setIsDraftState,
			setConfluenceData,
		},
	] = useDefaultStoreHook();
	const { closeRightSidebar, openRightSidebar } = useRightSidebarController();
	// Captured from the writer's issue context (this hook is consumed inside the
	// issue/work-item provider tree). Behind `remove-issue-context-mirror-on-wiv`,
	// persisting it on open lets the layout-mounted panel reader report the correct
	// analytics surface without reading the issue-context store, which is empty for
	// layout-level consumers once the mirror is gone. (Read is inert when the gate
	// is off — nothing is stored.)
	const analyticsSource = useAnalyticsSource();

	const openSidePanel = useCallback(
		({
			contentId,
			contentType,
			data,
			hostname,
			isDraft,
			isLive,
			onLinkPage,
			onPublish,
			openInModal,
			mode,
			url,
			title,
		}: OpenSidePanelCoreParams) => {
			const { initialWidth, displayType } = getDisplayInformation();
			const shouldOpenPanel = displayType === DisplayTypes.PANEL;

			if (shouldOpenPanel) {
				openSidePanelAction({
					contentId,
					contentType,
					embeddedConfluenceSource,
					analyticsSource: fg('remove-issue-context-mirror-on-wiv') ? analyticsSource : undefined,
					data,
					displayType,
					initialWidth,
					hostname,
					isDraft,
					isLive,
					mode,
					onLinkPage,
					onPublish,
					shouldOpenModal: !shouldOpenPanel,
					url,
					title,
				});
				openRightSidebar(EMBEDDED_CONFLUENCE_SIDEPANEL_CONTAINER_ID, initialWidth, false, true);
			} else {
				openInModal();
			}
		},
		[analyticsSource, embeddedConfluenceSource, openRightSidebar, openSidePanelAction],
	);

	const closeSidePanel = useCallback(() => {
		closeRightSidebar(EMBEDDED_CONFLUENCE_SIDEPANEL_CONTAINER_ID);
		closeSidePanelAction();
	}, [closeRightSidebar, closeSidePanelAction]);

	return [
		state,
		{
			openSidePanel,
			closeSidePanel,
			setDeferredSpaceData,
			setIsLoadingForDeferSpaceSelection,
			toggleExpandedState,
			setIsDraftState,
			setConfluenceData,
		},
	];
};
